import React, { useEffect, useState } from "react";
import { Tabs, Avatar } from "antd";
import { MailOutlined } from "@ant-design/icons";
import AuthService from "../../Services/auth.service";
import ProfileMatcherService from "../../Services/profile.matcher.service";
import { Userprofile } from "../../Entities/user-profile.entity";
import { openNotification } from "../../Components/Notification/Index";
import ApplicationStatsCards from "./components/ApplicationStatsCards";
import { useCandidateAnalytics } from "../../Hooks/useCandidateAnalytics";
import ApplicationFunnel from "./components/ApplicationFunnel";
import ApplicationTable from "./components/ApplicationTable";
import ResumeList from "./components/ResumeList";
import { useLoader } from "../../Context/LoaderContext/LoaderContext";
import "./index.scss";

const Profile:React.FC = () => {
  const user = AuthService.getInstance().getCurrentUser();
  const [resumes, setResumes] = useState<Userprofile[]>([]);
  const { applications } = useCandidateAnalytics();

  const { setLoading, setLoadingMessage } = useLoader();

  useEffect(() => {
    if (user) {
      fetchDirectUploadResumes();
    }
  }, [user]);

  const fetchDirectUploadResumes = async () => {
    try {
      setLoading(true);
      setLoadingMessage("Loading please wait...");
      const profiles = await new ProfileMatcherService().getAllUploadedProfiles(
        true,
      );
      const directUploadProfiles =
        profiles?.filter((profile: Userprofile) => profile.isDirectUpload) ||
        [];
      const sortedResumes = directUploadProfiles.sort(
        (a: Userprofile, b: Userprofile) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setResumes(sortedResumes);
    } catch (error) {
      console.error("Error fetching resumes:", error);
      openNotification("error", "Error", "Failed to load resumes");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    return user?.firstName?.charAt(0).toUpperCase() || "U";
  };

  const getFullName = () => {
    if (user?.firstName) {
      const firstName =
        user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1);
      const lastName = user.lastName
        ? user.lastName.charAt(0).toUpperCase() + user.lastName.slice(1)
        : "";
      return `${firstName} ${lastName}`.trim();
    }
    return "User";
  };

  const tabItems = [
    {
      key: "applications",
      label: "Recent Applications",
      children: <ApplicationTable applications={applications} />,
    },
    {
      key: "resumes",
      label: "My Resumes",
      children: <ResumeList resumes={resumes} />,
    },
  ];

  return (
    <main className="profile">
      <div className="profile__container">
        {/* Header */}
        <header className="profile__header">
          <div className="profile__user-info">
            <Avatar size={100} className="profile__avatar">
              {getInitials()}
            </Avatar>
            <div className="profile__user-meta">
              <h1 className="profile__name">{getFullName()}</h1>
              <p className="profile__email">
                <MailOutlined className="profile__email-icon" />
                {user?.email}
              </p>
              <div className="profile__badges">
                <span className="profile__badge profile__badge--open">
                  Candidate
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Stats Grid */}
        <ApplicationStatsCards applications={applications} />

        {/* Application Funnel */}
        <ApplicationFunnel applications={applications} />

        {/* Tabs & Table */}
        <section className="profile__tabs-section">
          <Tabs items={tabItems} className="profile__tabs" />
        </section>
      </div>
    </main>
  );
};

export default Profile;
